import DocShell, { Clause } from '@/components/rack/DocShell';

/**
 * Cookie 与本地存储。
 *
 * 旧版是模板文本：声称使用「必要 / 偏好 / 分析」三类 Cookie，还写了第三方分析 Cookie。
 * 实际情况是：仓库里有 utils/analytics.ts，但没有任何地方调用它，index.html 里也没有
 * 统计脚本——站上从来没发出过一次统计请求。写了不存在的收集行为，等于给自己挖坑。
 *
 * 这页的主体因此是一张对照表：浏览器里到底存了什么。真要核对，源码里搜得到，
 * 浏览器 DevTools 里也看得到——这是这页唯一值得存在的理由。
 * 以后真接了统计，先改这页再上线。
 */

/** 站点实际写入浏览器的东西。加新条目前先确认它真的会被写入 */
const stored: [string, string, string, string][] = [
  ['i18nextLng', '界面语言（默认中文）', 'localStorage', '直到你清除浏览器数据'],
];

export default function CookiePolicy() {
  return (
    <DocShell
      code="COOKIES"
      title="Cookie 与本地存储"
      updated="2026-09-01"
      thesis="这站没有埋点"
      lede="多数 Cookie 政策写得像法律模板，说自己用了必要、偏好、分析三类 Cookie。这站的实际情况比那简单得多：不放广告、不接统计脚本，浏览器里只留一条记你选了哪种语言的记录。下面逐条对照，源码和浏览器里都能核。"
      description="minddock.ai 的 Cookie 与本地存储说明：站点不投放广告、不接入统计脚本，仅在 localStorage 存一条界面语言记录。"
      path="/cookies"
    >
      <div className="mt-4 border-t border-rack-bone/12">
        <Clause no="§01" title="实际存了什么">
          <p>就一条。存在 localStorage 里，不是 Cookie：</p>

          <div className="!mt-5 overflow-x-auto">
            <table className="w-full min-w-[520px] border-collapse text-left">
              <thead>
                <tr className="border-b border-rack-bone/12">
                  {['键名', '存什么', '存在哪', '存多久'].map((h) => (
                    <th
                      key={h}
                      scope="col"
                      className="pb-2.5 font-readout text-[9.5px] font-normal uppercase tracking-[0.16em] text-rack-bone/28"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {stored.map(([key, what, where, how]) => (
                  <tr key={key} className="border-b border-rack-bone/[0.07]">
                    <td className="py-3 pr-5 font-readout text-[12.5px] text-rack-bone/85">{key}</td>
                    <td className="py-3 pr-5 text-[12.5px] text-rack-bone/60">{what}</td>
                    <td className="py-3 pr-5 font-readout text-[11px] uppercase tracking-[0.1em] text-rack-bone/45">
                      {where}
                    </td>
                    <td className="py-3 text-[12.5px] text-rack-bone/60">{how}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-[12px] text-rack-bone/38">
            localStorage 不是 Cookie：它不会随每次请求发到服务器，也不能被别的网站读取。
            它只让你下次打开时还是同一种语言。
          </p>
        </Clause>

        <Clause no="§02" title="没有的东西">
          <p>
            没有广告 Cookie，没有第三方追踪像素，没有 Google Analytics 或同类统计脚本，
            没有跨站行为跟踪，也没有用户画像。
          </p>
          <p>
            这不是承诺，是当前的事实：页面源码里搜不到统计脚本，浏览器 DevTools 的网络面板里
            也看不到统计请求。你可以自己核。
          </p>
        </Clause>

        <Clause no="§03" title="托管商可能设置的 Cookie">
          <p>
            站点托管在 Cloudflare。出于防攻击与机器人识别，Cloudflare 可能设置必要 Cookie
            （如 <span className="font-readout text-rack-bone/70">__cf_bm</span>）。
            这类 Cookie 只服务于安全与可用性，不用于分析你的行为，具体内容与时长由 Cloudflare 决定，
            不由我配置。
          </p>
        </Clause>

        <Clause no="§04" title="怎么清掉">
          <p>
            在浏览器设置里清除本站数据即可，页面会回到默认中文，其它没有任何影响；
            也不需要先来问我。
          </p>
          <p>
            这也是站上没有「同意 Cookie」横幅的原因——没有需要你同意的追踪，
            弹一个横幅只是在浪费你一次点击。
          </p>
        </Clause>

        <Clause no="§05" title="以后如果接了统计">
          <p>
            先改这一页、更新顶部日期，写明用的是什么工具、收哪些字段、留多久，再上线。
            顺序不会反过来。
          </p>
        </Clause>
      </div>
    </DocShell>
  );
}
